import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { apiClient } from '@services/api'

interface StoreRow {
  id: number
  name: string
  location: string | null
  route: string | null
  totalSalesMtd: number
  turnoverPackets: number
  grossProfitMtd: number
  returnsMtd: number
  profit: number
  outstanding: number
  overdue: number
  overdueDays: number
  reliabilityGrade: 'A' | 'B' | 'C' | 'New'
  onTimePct: number | null
  invoices: number
  highOverdue: boolean
  lossMaking: boolean
  flagged: boolean
}
interface InvoiceOption { id: number; invoiceNo: string; invoiceDate: string; amount: number; balance: number; paymentStatus: string }
interface InvoiceLine { product: string; packG: number; unit: string; label: string; quantity: number; rate: number }
interface CreditNote { id: number; creditNoteNo: string; store: string; invoiceNo: string; label: string; quantity: number; unit: string; amount: number; appliedAmount: number; storeCredit: number; reason: string; restocked: boolean; date: string }

type SortKey = 'overdue' | 'profit' | 'sales' | 'turnover' | 'grade' | 'name' | 'outstanding'
type Chip = 'all' | 'highOverdue' | 'lossMaking' | 'A' | 'B' | 'C'
const GRADE_ORDER: Record<string, number> = { C: 0, B: 1, A: 2, New: 3 }
const formatINR = (n: number) => `${n < 0 ? '−' : ''}₹${Math.abs(Math.round(n)).toLocaleString('en-IN')}`
const formatDate = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
const today = () => new Date().toISOString().split('T')[0]

export function StoreReliability() {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<SortKey>('overdue')
  const [chip, setChip] = useState<Chip>('all')

  const { data, isLoading, refetch } = useQuery({ queryKey: ['store-reliability'], queryFn: () => apiClient.get<any>('/store-reliability'), refetchInterval: 60000 })
  const rows: StoreRow[] = data?.data?.items || []
  const summary = data?.data?.summary
  const reasons: string[] = data?.data?.reasons || ['Damaged packaging', 'Near expiry — store declined', 'Wrong item delivered', 'Other']
  const { data: cnData, refetch: refetchCn } = useQuery({ queryKey: ['credit-notes'], queryFn: () => apiClient.get<any>('/credit-notes?limit=10') })
  const creditNotes: CreditNote[] = cnData?.data?.items || []

  const visible = useMemo(() => {
    let r = rows
    const s = search.trim().toLowerCase()
    if (s) r = r.filter((x) => x.name.toLowerCase().includes(s) || (x.location || '').toLowerCase().includes(s) || (x.route || '').toLowerCase().includes(s))
    if (chip === 'highOverdue') r = r.filter((x) => x.highOverdue)
    else if (chip === 'lossMaking') r = r.filter((x) => x.lossMaking)
    else if (chip === 'A' || chip === 'B' || chip === 'C') r = r.filter((x) => x.reliabilityGrade === chip)
    const cmp: Record<SortKey, (a: StoreRow, b: StoreRow) => number> = {
      overdue: (a, b) => b.overdue - a.overdue || b.overdueDays - a.overdueDays,
      profit: (a, b) => a.profit - b.profit,
      sales: (a, b) => b.totalSalesMtd - a.totalSalesMtd,
      turnover: (a, b) => b.turnoverPackets - a.turnoverPackets,
      grade: (a, b) => GRADE_ORDER[a.reliabilityGrade] - GRADE_ORDER[b.reliabilityGrade] || b.overdue - a.overdue,
      name: (a, b) => a.name.localeCompare(b.name),
      outstanding: (a, b) => b.outstanding - a.outstanding,
    }
    return [...r].sort(cmp[sort])
  }, [rows, search, chip, sort])

  // ---- Goods return → credit note ----
  const [retStore, setRetStore] = useState('')
  const [retInvoice, setRetInvoice] = useState('')
  const [retItem, setRetItem] = useState('')
  const [retQty, setRetQty] = useState('')
  const [retReason, setRetReason] = useState('Damaged packaging')
  const [restock, setRestock] = useState<boolean | null>(null)
  const [preview, setPreview] = useState<{ ok: boolean; text: string } | null>(null)
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; text: string; link?: string } | null>(null)

  const { data: invData } = useQuery({ queryKey: ['store-invoices', retStore], queryFn: () => apiClient.get<any>(`/invoices?storeId=${retStore}`), enabled: !!retStore })
  const invoices: InvoiceOption[] = (invData?.data?.items || []).filter((i: any) => i.paymentStatus !== 'Cancelled')
  const { data: invDetail } = useQuery({ queryKey: ['invoice', retInvoice], queryFn: () => apiClient.get<any>(`/invoices/${retInvoice}`), enabled: !!retInvoice })
  const lines: InvoiceLine[] = invDetail?.data?.lineItems || []
  const line = lines.find((l) => `${l.product}|${l.packG}` === retItem) || null
  const defaultRestock = retReason === 'Wrong item delivered' || retReason === 'Other'
  const effectiveRestock = restock === null ? defaultRestock : restock

  useEffect(() => { setRetInvoice(''); setRetItem(''); setRetQty(''); setPreview(null) }, [retStore])
  useEffect(() => { setRetItem(''); setRetQty(''); setPreview(null) }, [retInvoice])
  useEffect(() => {
    if (!retInvoice || !line || !(Number(retQty) > 0)) { setPreview(null); return }
    const t = setTimeout(async () => {
      try {
        const res = await apiClient.post<any>('/credit-notes/preview', { invoiceId: Number(retInvoice), product: line.product, packG: line.packG, quantity: Number(retQty), reason: retReason, restock: effectiveRestock })
        setPreview({ ok: true, text: res.data.message })
      } catch (err: any) {
        setPreview({ ok: false, text: err.response?.data?.message || 'Cannot process this return' })
      }
    }, 250)
    return () => clearTimeout(t)
  }, [retInvoice, retItem, retQty, retReason, effectiveRestock, line?.product])

  const processReturn = async () => {
    setNotice(null)
    if (!retStore) return setPreview({ ok: false, text: 'Select the store' })
    if (!retInvoice) return setPreview({ ok: false, text: 'Select the invoice the goods were billed on' })
    if (!line) return setPreview({ ok: false, text: 'Select the item being returned' })
    if (!(Number(retQty) > 0)) return setPreview({ ok: false, text: 'Enter the quantity returned' })
    setSaving(true)
    try {
      const res = await apiClient.post<any>('/credit-notes', { invoiceId: Number(retInvoice), product: line.product, packG: line.packG, quantity: Number(retQty), reason: retReason, restock: effectiveRestock, date: today() })
      setNotice({ kind: 'success', text: res.message, link: `/dashboard/sales/invoices/${retInvoice}` })
      setRetStore(''); setRetReason('Damaged packaging'); setRestock(null); setPreview(null)
      await Promise.all([refetch(), refetchCn()])
      queryClient.invalidateQueries({ queryKey: ['stores'] })
      queryClient.invalidateQueries({ queryKey: ['stock-overview'] })
      queryClient.invalidateQueries({ queryKey: ['collections-summary'] })
    } catch (err: any) {
      setNotice({ kind: 'error', text: err.response?.data?.message || 'Could not process the return' })
    } finally {
      setSaving(false)
    }
  }

  const th = (label: string, key: SortKey, num = false) => (
    <th className={`${num ? 'num ' : ''}sortable ${sort === key ? 'active' : ''}`} onClick={() => setSort(key)}>{label}</th>
  )

  return (
    <div>
      <div className="hdr">
        <h1>Store Reliability &amp; Performance</h1>
        <p>Every store's sales, credit, overdue, turnover, and profit in one place — sort or filter to spot who's high-risk or loss-making.</p>
      </div>

      <div className="toolbar">
        <input type="text" placeholder="Search store name..." value={search} onChange={(e) => setSearch(e.target.value)} />
        <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
          <option value="overdue">Sort by: Overdue Amount (high → low)</option>
          <option value="profit">Sort by: Profit Earned (low → high)</option>
          <option value="sales">Sort by: Total Sales (high → low)</option>
          <option value="turnover">Sort by: Turnover (high → low)</option>
          <option value="grade">Sort by: Reliability Grade</option>
        </select>
        <button type="button" className={`chip ${chip === 'all' ? 'on' : ''}`} onClick={() => setChip('all')}>All Stores{summary ? ` (${summary.stores})` : ''}</button>
        <button type="button" className={`chip danger-chip ${chip === 'highOverdue' ? 'on' : ''}`} onClick={() => setChip(chip === 'highOverdue' ? 'all' : 'highOverdue')}>High Overdue (&gt;30d){summary ? ` (${summary.highOverdue})` : ''}</button>
        <button type="button" className={`chip danger-chip ${chip === 'lossMaking' ? 'on' : ''}`} onClick={() => setChip(chip === 'lossMaking' ? 'all' : 'lossMaking')}>Loss-Making{summary ? ` (${summary.lossMaking})` : ''}</button>
        {(['A', 'B', 'C'] as const).map((g) => (
          <button key={g} type="button" className={`chip ${chip === g ? 'on' : ''}`} onClick={() => setChip(chip === g ? 'all' : g)}>Grade {g}{summary ? ` (${summary.grades[g]})` : ''}</button>
        ))}
      </div>

      <div className="card">
        <table>
          <thead>
            <tr>
              {th('Store', 'name')}
              <th>Route / Location</th>
              {th('Total Sales (MTD)', 'sales', true)}
              {th('Turnover (packets)', 'turnover', true)}
              {th('Profit Earned', 'profit', true)}
              {th('Credit Outstanding', 'outstanding', true)}
              {th('Overdue', 'overdue', true)}
              <th>Reliability</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={8} className="text-ink-soft">Loading…</td></tr>}
            {!isLoading && visible.length === 0 && <tr><td colSpan={8} className="text-ink-soft">No stores match.</td></tr>}
            {visible.map((s) => (
              <tr key={s.id} className={s.flagged ? 'flag-row' : ''}>
                <td>
                  <div className="font-medium">{s.name}</div>
                  <div className="text-[10.5px] text-ink-soft">{s.invoices} invoice{s.invoices === 1 ? '' : 's'}{s.onTimePct !== null ? ` · ${s.onTimePct}% on-time` : ''}{s.returnsMtd > 0 ? ` · returns ${formatINR(s.returnsMtd)}` : ''}</div>
                </td>
                <td>{s.location || '—'}{s.route && s.route !== `${s.location} Route` ? <div className="text-[10.5px] text-ink-soft">{s.route}</div> : null}</td>
                <td className="num">{formatINR(s.totalSalesMtd)}</td>
                <td className="num">{s.turnoverPackets.toLocaleString('en-IN')}</td>
                <td className={`num ${s.profit < 0 ? 'text-danger font-semibold' : ''}`}>{formatINR(s.profit)}</td>
                <td className="num">{formatINR(s.outstanding)}</td>
                <td className={`num ${s.overdue > 0 ? (s.highOverdue ? 'text-danger font-semibold' : 'font-semibold') : ''}`}>{s.overdue > 0 ? `${formatINR(s.overdue)} · ${s.overdueDays}d` : '—'}</td>
                <td><span className={`rel-badge ${s.reliabilityGrade === 'New' ? 'B' : s.reliabilityGrade}`}>{s.reliabilityGrade === 'New' ? 'New' : `Grade ${s.reliabilityGrade}`}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="hint" style={{ marginTop: 10 }}>Red rows: overdue past 30 days or currently loss-making after accounting for returns. Click a column header to sort.</div>
      </div>

      {notice && (
        <div className={`alert ${notice.kind === 'success' ? 'alert-success' : 'alert-error'}`} style={{ marginTop: 18 }}>
          {notice.text}{notice.link && <> · <Link to={notice.link} className="font-semibold underline">Open invoice</Link></>}
        </div>
      )}

      <div className="an-grid2" style={{ alignItems: 'stretch' }}>
        <div className="card">
          <h2>Process a Goods Return</h2>
          <label>Store</label>
          <select value={retStore} onChange={(e) => setRetStore(e.target.value)}>
            <option value="">— Select store —</option>
            {rows.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <label>Invoice</label>
          <select value={retInvoice} onChange={(e) => setRetInvoice(e.target.value)} disabled={!retStore}>
            <option value="">— Select invoice —</option>
            {invoices.map((i) => <option key={i.id} value={i.id}>{i.invoiceNo} — {formatDate(i.invoiceDate)} — {formatINR(i.amount)}{i.balance > 0 ? ` (balance ${formatINR(i.balance)})` : ' (paid)'}</option>)}
          </select>
          <div className="row2">
            <div>
              <label>Item</label>
              <select value={retItem} onChange={(e) => { setRetItem(e.target.value); setRetQty('') }} disabled={!retInvoice}>
                <option value="">— Select item —</option>
                {lines.map((l) => <option key={`${l.product}|${l.packG}`} value={`${l.product}|${l.packG}`}>{l.label} — {l.quantity} {l.unit} @ ₹{l.rate}</option>)}
              </select>
            </div>
            <div>
              <label>Qty Returned</label>
              <input type="number" min={1} step={1} value={retQty} onChange={(e) => setRetQty(e.target.value)} disabled={!line} placeholder={line ? `up to ${line.quantity}` : ''} />
            </div>
          </div>
          <label>Reason</label>
          <select value={retReason} onChange={(e) => { setRetReason(e.target.value); setRestock(null) }}>
            {reasons.map((r) => <option key={r}>{r}</option>)}
          </select>
          <label className="flex items-center gap-2 cursor-pointer mt-2">
            <input type="checkbox" checked={effectiveRestock} onChange={(e) => setRestock(e.target.checked)} />
            <span>Return the items to sellable stock <small>— {defaultRestock ? 'on by default for this reason' : 'off by default: damaged / near-expiry goods are written off'}</small></span>
          </label>
          {preview ? <div className={preview.ok ? 'hint calc' : 'hint-error'}>{preview.text}</div> : <div className="hint">Pick the invoice, item and quantity — the credit note amount is calculated at the invoiced rate.</div>}
          <div className="btnrow"><button type="button" className="btn-accent" onClick={processReturn} disabled={saving || (preview !== null && !preview.ok)}>{saving ? 'Processing…' : 'Process Return & Issue Credit Note'}</button></div>
        </div>
        <div className="card">
          <h2>Recent Credit Notes</h2>
          <table>
            <thead><tr><th>Credit Note</th><th>Store</th><th className="num">Amount</th><th>Reason</th></tr></thead>
            <tbody>
              {creditNotes.length === 0 && <tr><td colSpan={4} className="text-ink-soft">No credit notes yet.</td></tr>}
              {creditNotes.map((c) => (
                <tr key={c.id}>
                  <td><div className="font-medium">{c.creditNoteNo}</div><div className="text-[10.5px] text-ink-soft">{formatDate(c.date)} · {c.invoiceNo} · {c.quantity} {c.unit} {c.label}{c.restocked ? ' · restocked' : ''}</div></td>
                  <td>{c.store}</td>
                  <td className="num">{formatINR(c.amount)}{c.storeCredit > 0 && <div className="text-[10.5px] text-ink-soft">{formatINR(c.storeCredit)} store credit</div>}</td>
                  <td>{c.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="hint">Credit notes reduce the invoice balance on the store's ledger; any amount beyond the balance is carried as store credit.</div>
        </div>
      </div>
    </div>
  )
}
